import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { ROUTE_PATH_LIST_AUTH } from '../../../../constants/route'
import { useInitData, useInlineNotifications } from '../../../../hooks'
import { parseServerValidation } from '../../../../utils/validation'
import { FormikForm } from '../../../Formik'
import { register } from '../../Auth.api'
import { hasNotifications } from '../../Auth.utils'
import { AuthFooter } from '../../AuthFooter'
import { AuthFormWrapper } from '../../AuthFormWrapper'
import { AuthHeader } from '../../AuthHeader'
import { AuthHelpText } from '../../AuthHelpText'
import { AuthLicense } from '../../AuthLicense'
import { AuthPanel } from '../../AuthPanel'
import { AuthTitle } from '../../AuthTitle'

import { REGISTRATION_FORM_INITIAL_VALUES } from './RegistrationForm.constants'
import {
  getRegistrationFormPayload,
  getRegistrationFormValidationSchema,
  parseRegistrationFormServerError,
} from './RegistrationForm.utils'
import { RegistrationFormFields } from './RegistrationFormFields'

import * as styles from './RegistrationForm.module.css'

function RegistrationForm({
  getValidationSchema = getRegistrationFormValidationSchema,
  initialValues = REGISTRATION_FORM_INITIAL_VALUES,
}) {
  const { t } = useTranslation()

  const initData = useInitData()

  const registrationEnabled = initData?.uk?.config?.free_registration

  const { handleAddNotification, handleRemoveNotification, notifications } =
    useInlineNotifications()

  const [createdUserData, setCreatedUserData] = useState(null)

  function handleSubmit(values, { setErrors, setSubmitting }) {
    register(getRegistrationFormPayload(values))
      .then(response => {
        setCreatedUserData(response.data)
      })
      .catch(error => {
        setErrors(
          parseServerValidation(parseRegistrationFormServerError(error))
        )
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const validationSchema = getValidationSchema()

  const withNotifications = hasNotifications(notifications)

  const titleClassName = classNames(styles.title, {
    [styles.withNotifications]: withNotifications,
  })

  return (
    <AuthPanel withNotifications={withNotifications}>
      <AuthHeader
        description={t('Auth.SignInDescriptionKey')}
        link={{
          text: t('Auth.SignInLinkKey'),
          to: ROUTE_PATH_LIST_AUTH.login(),
        }}
        notifications={notifications}
      />
      <AuthFormWrapper>
        <AuthTitle
          className={titleClassName}
          title={t(
            createdUserData
              ? 'Auth.SignUpCompletedTitleKey'
              : 'Auth.SignUpTitleKey'
          )}
        />
        {registrationEnabled ? (
          createdUserData ? (
            <AuthHelpText
              text={
                <Trans
                  components={{ strong: <strong /> }}
                  i18nKey='Auth.SignUpCompletedTextKey'
                  values={{ email: createdUserData.email }}
                />
              }
            />
          ) : (
            <FormikForm
              initialValues={initialValues}
              validateOnBlur={false}
              validateOnChange={false}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              <RegistrationFormFields
                onRemoveNotification={handleRemoveNotification}
                onSetNotification={handleAddNotification}
              />
            </FormikForm>
          )
        ) : (
          <AuthHelpText
            text={t('Auth.SignUpFreeRegistrationIsRestrictedTextKey')}
          />
        )}
        {!createdUserData && registrationEnabled ? (
          <AuthLicense className={styles.license} />
        ) : null}
      </AuthFormWrapper>
      <AuthFooter withNotifications={withNotifications} />
    </AuthPanel>
  )
}

export default RegistrationForm
